import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "../lib/api";
import Logo from "../components/Logo";

// ──────────────────────────────────────────────────────────
// The Root Forum — Public Marketing Site
// ──────────────────────────────────────────────────────────

const NAV = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Team", href: "#team" },
  { label: "Pricing", href: "#pricing" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

const MARQUEE_ITEMS = ["Allergies", "Emotion Code", "Infrared Sauna", "Bio-Frequency", "Bloodwork", "Body Code"];

const ABOUT_CHECKS = [
  {
    n: "01",
    title: "Pain-Free Method",
    body: "Muscle testing and frequency-based allergy clearing — no needles, no medications.",
  },
  {
    n: "02",
    title: "Whole-Family Care",
    body: "Children, parents, anyone navigating chronic symptoms — we meet you where you are.",
  },
  {
    n: "03",
    title: "Root, Not Symptom",
    body: "We don't band-aid. We find the underlying cause and rebuild from the inside out.",
  },
];

const WHY_PILLS = [
  { title: "Personalized Care", body: "Every person is unique. So is every protocol we build." },
  { title: "Root Cause Focus", body: "We chase the source, not the symptom." },
  { title: "Collaborative", body: "We work alongside your other practitioners." },
  { title: "Empowerment", body: "You leave with the tools to keep healing." },
];

const STATS = [
  { num: "60", suffix: "+", label: "Years of Protocol" },
  { num: "1.2k", suffix: "+", label: "Families Served" },
  { num: "25", suffix: "hr", label: "Post-Session Window" },
  { num: "1", suffix: "", label: "Charge Per Allergen" },
];

const TEAM = [
  {
    initials: "JB",
    name: "Jennie Burks",
    role: "Emotion & Body Code Practitioner",
    bio: "Wife to Tyrre, mommy to Marcus, Brooklyn, and Julian. Through her family's unexpected journey with food and environmental allergies, Jennie's mission became giving every child a chance at a peaceful, purposeful life.",
    hours: "Available Mondays & Fridays",
    bg: "from-tf-green-700 to-tf-green-800",
    photo: "https://customer-assets.emergentagent.com/job_9a92cfa8-cf5a-4019-8f5e-7cc7c17938c6/artifacts/j81i3pfg_IMG_5988.jpg",
  },
  {
    initials: "KD",
    name: "Kendra Davis",
    role: "Emotion Code Practitioner",
    bio: "Wife, mother, and lover of all things holistic. Passionate about real food, natural living, and raising a family grounded in faith. Her path into wellness grew from navigating health challenges with her own children.",
    hours: "Available by appointment on Thursdays",
    bg: "from-tf-wood to-[#a07c4f]",
    photo: "https://customer-assets.emergentagent.com/job_9a92cfa8-cf5a-4019-8f5e-7cc7c17938c6/artifacts/wq22dds7_Kendra.png",
  },
  {
    initials: "JY",
    name: "Jaylin Baah-Yeboah",
    role: "Emotion Code Practitioner",
    bio: "A mother and dedicated advocate for healing and wholeness. Her own healing journey — guided by prayer and faith — inspired her passion for helping others release emotional burdens and move toward renewed well-being.",
    hours: "Available by appointment on Wednesdays",
    bg: "from-tf-green-300 to-tf-green-500",
    photo: "https://customer-assets.emergentagent.com/job_9a92cfa8-cf5a-4019-8f5e-7cc7c17938c6/artifacts/tg9ovsrj_1F8A2682.jpeg",
  },
];

const TESTIMONIALS = [
  { quote: "My quality of life was a 3, and now it is a 10. I feel like a new man.", author: "D.B. — Minneapolis" },
  { quote: "My daughter is no longer anaphylactic to eggs and dairy. Our lives are forever changed.", author: "Parent of Patient", feature: true },
  { quote: "I can mow the lawn without having to jump straight in the shower afterward. Spring is now my favorite season.", author: "Brad S. — Elk River" },
  { quote: "My daughter was waking up every hour. After two sessions with Jennie, she sleeps through the night.", author: "A Grateful Mama" },
];

const FAQ = [
  {
    q: "What happens during an initial allergy appointment?",
    a: "Your 90-minute initial appointment includes a comprehensive health assessment, lifestyle review, and muscle testing to identify potential triggers and imbalances. From there we map out a plan to clear allergies one at a time. Most allergens clear in a single 25-hour session.",
  },
  {
    q: "Do you accept health insurance?",
    a: "We do not accept health insurance, but we offer affordable payment plans for allergy patients. The good news: each allergen is one flat charge, regardless of how many sessions it takes to fully neutralize.",
  },
  {
    q: "How many sessions will my child need?",
    a: "Each allergen is worked on individually — one per session, with at least 25 hours between sessions. If your child tests positive for 15 allergens, that's roughly 15 visits. Three months after clearing, we re-screen at no additional charge.",
  },
  {
    q: "Is the technique painful? Are there needles?",
    a: "No. The Wilson Protocol is entirely pain-free and pharmaceutical-free. No skin pricks, no blood draws, no medications. We use muscle testing and a combined-modality approach drawn from Acupuncture, Functional Medicine, NAET, JMT, and Kinesiology.",
  },
  {
    q: "Can Emotion Code sessions be done remotely?",
    a: "Yes. Emotion Code and Body Code sessions are offered both in person at our Apple Valley office and remotely. Many of our patients book remote sessions for convenience.",
  },
  {
    q: "Do you diagnose or treat disease?",
    a: "No. We do not diagnose, treat, or claim to cure any disease. We support the body's natural ability to regain balance by addressing the underlying factors that contribute to overreactive immune responses and other imbalances.",
  },
];

// Static services (the design uses 6 marketing cards). Backend `name` matches a
// seeded service so each card links to its own booking page.
const SERVICES = [
  {
    num: "01 / Allergy Care",
    title: "The Wilson Protocol",
    body: "A pain-free, pharmaceutical-free technique that clears chronic allergies one allergen at a time. Most clear in a single session.",
    backendName: "Allergy Panel",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2 L12 22 M2 12 L22 12 M5 5 L19 19 M19 5 L5 19" />
      </svg>
    ),
  },
  {
    num: "02 / Energy Work",
    title: "Emotion & Body Code",
    body: "Identify and release trapped emotions, misalignments, and energetic blocks. Sessions offered remote or in person.",
    backendName: "Emotion / Body Code",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" />
      </svg>
    ),
  },
  {
    num: "03 / Detox & Recovery",
    title: "Infrared Sauna Pod",
    body: "Reduce inflammation, improve sleep, calm the nervous system. Tera Hertz technology and red light therapy.",
    backendName: "Infrared Sauna Pod",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="5" />
        <path d="M12 2v2 M12 20v2 M4 12H2 M22 12h-2 M5 5l1.5 1.5 M17.5 17.5L19 19 M5 19l1.5-1.5 M17.5 6.5L19 5" />
      </svg>
    ),
  },
  {
    num: "04 / Diagnostics",
    title: "Bio-Frequency Scan",
    body: "Skip the guesswork. Measure and analyze your body's subtle energy fields for personalized insight.",
    backendName: "Bio-Frequency Scan",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 12h3l3-9 6 18 3-9h3" />
      </svg>
    ),
  },
  {
    num: "05 / Lab Work",
    title: "Functional Bloodwork",
    body: "An 80-test profile examining deep body systems, nutrient deficiencies, and root-cause indicators.",
    backendName: "Functional Medicine 80-Test Blood Profile",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 11l3 3 8-8 M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
      </svg>
    ),
  },
];

const PRICING_GROUPS = [
  {
    num: "01",
    label: "Allergy Care",
    note: "Affordable payment plans available for allergy patients.",
    items: [
      { name: "Initial Appointment", meta: "90 minutes · comprehensive intake & first allergen", price: "$150" },
      { name: "Additional Allergen Sessions", meta: "Per allergen · one flat charge no matter the sessions", price: "$69" },
    ],
  },
  {
    num: "02",
    label: "Sauna & Recovery",
    note: "Included free with every Allergy and Emotion / Body Code package.",
    items: [
      { name: "Infrared Sauna Pod & Tera Hertz Machine", meta: "Included with packages", price: "Free" },
      { name: "Single Use", meta: "Per session", price: "$25" },
    ],
  },
  {
    num: "03",
    label: "Emotion / Body Code",
    note: "Remote or in-person · 40-minute sessions.",
    items: [
      { name: "Single Session", meta: "40 minutes", price: "$85" },
      { name: "3-Pack", meta: "Save $10 · most popular", price: "$245", featured: true },
      { name: "5-Pack", meta: "Save $30", price: "$395" },
      { name: "10-Pack", meta: "Save $100 · best value", price: "$750" },
    ],
  },
  {
    num: "04",
    label: "Diagnostics & Cleanses",
    note: "Add-ons for deeper insight and full-system support.",
    items: [
      { name: "Bio-Frequency Scan", meta: "60 minutes · full reading & recommendations", price: "$250" },
      { name: "Parasite Cleanse", meta: "Complete protocol", price: "$300" },
      { name: "Functional Medicine 80-Test Blood Profile", meta: "Deep-system labs", price: "$495" },
    ],
  },
];

const PHONE = "612-385-0045";
const PHONE_TEL = "+16123850045";
const ADDRESS = "15322 Galaxie Ave, Suite 210, Apple Valley, MN 55124";

export default function PublicHome() {
  // Fetch services (used to show live pricing/duration on service cards)
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services", { params: { only_active: true } })
      .then(({ data }) => setServices(data))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-tf-cream-50 text-tf-ink font-ui overflow-x-hidden">
      <Nav />
      <Hero />
      <Marquee />
      <QuoteBand />
      <About />
      <Services services={services} />
      <Why />
      <Team />
      <Testimonials />
      <Letter />
      <Pricing />
      <Contact />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}

// ─── Nav ───────────────────────────────────────────────────
function Nav() {
  return (
    <nav className="sticky top-0 z-50 bg-tf-cream-50/90 backdrop-blur-md border-b border-tf-ink/10" data-testid="public-nav">
      <div className="max-w-[1240px] mx-auto px-8 flex items-center justify-between py-4">
        <a href="/" className="flex items-center" data-testid="logo-home" aria-label="The Root Forum — home">
          <Logo size="md" theme="light" />
        </a>
        <ul className="hidden md:flex gap-9 text-sm text-tf-ink-soft">
          {NAV.map((item) => (
            <li key={item.label}>
              <a href={item.href} className="hover:text-tf-green-800 transition-colors" data-testid={`nav-${item.label.toLowerCase()}`}>{item.label}</a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-4">
          <a href={`tel:${PHONE_TEL}`} className="hidden md:flex items-center gap-1.5 text-sm text-tf-ink-soft hover:text-tf-green-800">
            <PhoneSvg /> {PHONE}
          </a>
          <a href="#contact" className={btnPrimary} data-testid="nav-contact">Contact Us →</a>
          <Link to="/admin/login" className="text-sm font-medium text-tf-ink-soft hover:text-tf-green-800 transition-colors" data-testid="nav-admin-login">
            Admin
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ──────────────────────────────────────────────────
function Hero() {
  return (
    <section className="bg-tf-cream-100 py-20 md:py-[80px] relative overflow-hidden">
      <div className="max-w-[1240px] mx-auto px-8 grid md:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <div className={eyebrow + " mb-6"}>★ Apple Valley, Minnesota</div>
          <h1 className="font-serif text-[clamp(2.8rem,5.6vw,4.8rem)] leading-[1.05] tracking-tight mb-7">
            Root Cause <span className="italic font-light text-tf-green-800">Care</span><br />
            For The Whole Family.
          </h1>
          <p className="text-[1.05rem] text-tf-ink-soft max-w-[480px] mb-9 leading-[1.6]">
            Pain-free allergy elimination, emotion code work, and holistic care for ADHD, sleep, autoimmune support, and the days your body just feels off.
          </p>
          <div className="flex gap-3.5 flex-wrap mb-11">
            <a href="#contact" className={btnPrimary} data-testid="hero-contact">Get in Touch →</a>
            <a href="#services" className={btnGhost}>Explore Services</a>
          </div>
          <div className="flex items-center gap-3.5 text-sm text-tf-ink-soft">
            <div className="flex" aria-hidden>
              {[
                "from-tf-green-500 to-tf-green-700",
                "from-tf-wood to-[#b89066]",
                "from-tf-green-300 to-tf-green-500",
                "from-tf-cream-200 to-tf-wood",
              ].map((g, i) => (
                <span key={i} className={`w-9 h-9 rounded-full border-2 border-tf-cream-100 bg-gradient-to-br ${g} ${i > 0 ? "-ml-2.5" : ""}`} />
              ))}
            </div>
            <div><b className="font-semibold text-tf-ink">1,200+</b> Families served · 60 yrs of protocol</div>
          </div>
        </div>

        <HeroVisual />
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative aspect-[1/1.1] hidden md:block">
      <div className="absolute inset-0 rounded-t-[50%] bg-tf-green-800 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_9a92cfa8-cf5a-4019-8f5e-7cc7c17938c6/artifacts/joaxnywz_Create_a_picture_of_frequency__Nano_Banana_2_91244.png"
          alt="Frequency tinctures and herbal blends on a wooden rack inside The Root Forum"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      {/* decorative leaves */}
      <svg viewBox="0 0 100 200" className="absolute -left-4 top-1/4 w-16 h-32 opacity-90" aria-hidden>
        <path d="M50 10 Q70 50 60 100 Q50 150 50 190" stroke="#2a4a37" strokeWidth="2" fill="none" />
        <ellipse cx="80" cy="60" rx="12" ry="5" fill="#355a44" transform="rotate(30 80 60)" />
        <ellipse cx="20" cy="100" rx="12" ry="5" fill="#355a44" transform="rotate(-30 20 100)" />
        <ellipse cx="80" cy="140" rx="12" ry="5" fill="#355a44" transform="rotate(30 80 140)" />
      </svg>
      <svg viewBox="0 0 100 200" className="absolute -right-4 bottom-1/4 w-16 h-32 opacity-80" aria-hidden>
        <path d="M50 190 Q30 150 40 100 Q50 50 50 10" stroke="#6b8a73" strokeWidth="2" fill="none" />
        <ellipse cx="25" cy="80" rx="14" ry="6" fill="#6b8a73" transform="rotate(-40 25 80)" />
        <ellipse cx="70" cy="120" rx="14" ry="6" fill="#6b8a73" transform="rotate(40 70 120)" />
        <ellipse cx="30" cy="150" rx="14" ry="6" fill="#6b8a73" transform="rotate(-40 30 150)" />
      </svg>
    </div>
  );
}

// ─── Marquee ───────────────────────────────────────────────
function Marquee() {
  const loop = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="bg-tf-green-900 text-tf-cream-50 py-5 overflow-hidden">
      <div className="tf-marquee-track">
        {loop.map((item, i) => (
          <span key={i} className="inline-flex items-center font-serif italic text-[1.5rem] px-9 opacity-90">
            {item}
            <span className="ml-9 text-tf-green-300 not-italic font-sans">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Quote band ────────────────────────────────────────────
function QuoteBand() {
  return (
    <section className="bg-tf-cream-50 py-20">
      <div className="max-w-[920px] mx-auto px-8 text-center">
        <blockquote className="font-serif text-[clamp(1.6rem,3vw,2.4rem)] leading-[1.3] tracking-tight text-tf-ink">
          <span className="text-tf-green-700 font-serif text-[3rem] leading-none">"</span>
          Allergies do not cause everything, but they can cause anything.
          <cite className="block not-italic mt-7 text-xs tracking-[0.22em] uppercase text-tf-ink-soft font-sans font-medium">— American College of Allergists</cite>
        </blockquote>
      </div>
    </section>
  );
}

// ─── About ─────────────────────────────────────────────────
function About() {
  return (
    <section id="about" className="bg-tf-cream-100 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8 grid md:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
        <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-tf-green-800">
          <img
            src="https://customer-assets.emergentagent.com/job_9a92cfa8-cf5a-4019-8f5e-7cc7c17938c6/artifacts/sniq9l6e_I_want_a_clean_Office_for_Func_Nano_Banana_2_17184.png"
            alt="The Apple Valley room at The Root Forum — desk, reading chair, and a wall of plants"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-4 left-4 text-[11px] tracking-[0.22em] uppercase text-tf-cream-100 font-mono drop-shadow">— Apple Valley Room</div>
        </div>

        <div>
          <div className={eyebrow + " mb-3"}>About The Root Forum</div>
          <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight mb-6">
            Nurturing health, <span className="italic font-light">as a whole.</span>
          </h2>
          <p className="text-tf-ink-soft mb-4 leading-[1.65]">
            We understand the challenges parents face when their children struggle with ADHD, allergies, sleep, or autoimmune disorders. And the days when you wake up with brain fog, aches, and itchy eyes wondering why.
          </p>
          <p className="text-tf-ink-soft mb-8 leading-[1.65]">
            Our mission: provide the knowledge, tools, and support to help you and your family thrive — without skin pricks, traumatizing blood draws, or pharmaceuticals.
          </p>
          <div className="space-y-5 mb-8">
            {ABOUT_CHECKS.map((c) => (
              <div key={c.n} className="flex gap-4">
                <div className="font-serif text-tf-green-700 text-[1.4rem] flex-shrink-0 w-10 leading-none pt-1">{c.n}</div>
                <div>
                  <strong className="block font-medium text-tf-ink mb-0.5">{c.title}</strong>
                  <span className="text-tf-ink-soft text-sm leading-[1.6]">{c.body}</span>
                </div>
              </div>
            ))}
          </div>
          <a href="#services" className={btnGhost}>Our Approach →</a>
        </div>
      </div>
    </section>
  );
}

// ─── Services ──────────────────────────────────────────────
function Services({ services }) {
  const findSvc = (name) => services.find((x) => x.name === name);
  return (
    <section id="services" className="bg-tf-cream-50 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="flex items-end justify-between gap-6 mb-14 flex-wrap">
          <div>
            <div className={eyebrow + " mb-3"}>Our Services</div>
            <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight">
              Many pathways to <span className="italic font-light">whole-body balance.</span>
            </h2>
          </div>
          <a href="#pricing" className={btnGhost}>See Pricing →</a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
          {SERVICES.map((s, i) => {
            const svc = findSvc(s.backendName);
            return (
              <article key={i} className="bg-tf-cream-100 rounded-xl p-7 transition-all hover:bg-tf-green-800 hover:text-tf-cream-50 group" data-testid={`service-${i}`}>
                <div className="w-11 h-11 rounded-full bg-tf-cream-50 group-hover:bg-tf-green-900 text-tf-green-800 group-hover:text-tf-cream-50 flex items-center justify-center mb-7 transition-colors">
                  <div className="w-[22px] h-[22px]">{s.icon}</div>
                </div>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-tf-ink-soft group-hover:text-tf-green-300 mb-3 transition-colors">{s.num}</div>
                <h3 className="font-serif text-[1.5rem] leading-[1.15] mb-3">{s.title}</h3>
                <p className="text-tf-ink-soft group-hover:text-tf-cream-200 text-[0.95rem] leading-[1.6] mb-4 transition-colors">{s.body}</p>
                {svc && (
                  <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-tf-green-700 group-hover:text-tf-green-300 mb-5">
                    <span>{formatPrice(svc.price)}</span>
                    {svc.duration_minutes > 0 && <span className="opacity-60">·</span>}
                    {svc.duration_minutes > 0 && <span>{svc.duration_minutes} min</span>}
                  </div>
                )}
                <a href="#contact" className="inline-flex items-center gap-1.5 text-sm font-medium border-b border-current pb-0.5" data-testid={`service-contact-${i}`}>Ask About This →</a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Why us ────────────────────────────────────────────────
function Why() {
  return (
    <section className="bg-tf-green-800 text-tf-cream-50 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="grid md:grid-cols-2 gap-16 items-start mb-16">
          <div>
            <div className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-tf-green-300 mb-3 font-medium">Why Choose Us</div>
            <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight mb-6">
              Care that <span className="italic font-light">listens first,</span><br />
              then helps your body remember.
            </h2>
            <p className="text-tf-cream-200/85 text-[1.05rem] leading-[1.65] max-w-[460px] mb-8">
              We believe trapped emotions, traumas, and over-reactive immune responses are at the underlying root of most modern health issues. When the body has the right information, it knows what to do.
            </p>
            <a href="#contact" className={btnCream}>Get in Touch →</a>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {WHY_PILLS.map((p) => (
              <div key={p.title} className="bg-tf-green-700/60 backdrop-blur rounded-lg p-6 border border-tf-green-300/15">
                <div className="w-10 h-10 rounded-full bg-tf-green-900 text-tf-green-300 flex items-center justify-center mb-4">
                  <CircleDot className="w-5 h-5" />
                </div>
                <h4 className="font-serif text-[1.15rem] mb-1.5">{p.title}</h4>
                <p className="text-tf-cream-200/75 text-sm leading-[1.55]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 border-t border-tf-green-300/15">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-[clamp(2.4rem,4vw,3.4rem)] leading-none mb-2">
                {s.num}<span className="text-tf-green-300 text-[0.55em] align-top ml-1">{s.suffix}</span>
              </div>
              <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-tf-green-300">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Team ──────────────────────────────────────────────────
function Team() {
  return (
    <section id="team" className="bg-tf-cream-50 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="text-center max-w-[640px] mx-auto mb-14">
          <div className={eyebrow + " mb-3"}>Meet Our Practitioners</div>
          <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight mb-5">
            The hands and hearts <span className="italic font-light">behind your care.</span>
          </h2>
          <p className="text-tf-ink-soft leading-[1.6]">
            A team of mothers and lifelong holistic practitioners trained in Emotion Code, Body Code, and the Wilson Protocol.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6" data-testid="team-grid">
          {TEAM.map((t) => (
            <article key={t.name} className="bg-tf-cream-100 rounded-xl p-7 text-center">
              {t.photo ? (
                <img
                  src={t.photo}
                  alt={t.name}
                  className="w-28 h-28 rounded-full object-cover mx-auto mb-5 ring-2 ring-tf-cream-50 shadow-sm"
                  loading="lazy"
                />
              ) : (
                <div className={`w-28 h-28 rounded-full bg-gradient-to-br ${t.bg} mx-auto mb-5 flex items-center justify-center text-tf-cream-50 font-serif text-2xl tracking-wide`}>
                  {t.initials}
                </div>
              )}
              <div className="font-serif text-[1.4rem] mb-1">{t.name}</div>
              <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-tf-green-700 mb-4">{t.role}</div>
              <p className="text-tf-ink-soft text-[0.92rem] leading-[1.6] mb-5">{t.bio}</p>
              <div className="inline-block px-3.5 py-1.5 rounded-full bg-tf-cream-50 text-xs text-tf-ink-soft font-medium">{t.hours}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Testimonials ──────────────────────────────────────────
function Testimonials() {
  return (
    <section id="testimonials" className="bg-tf-cream-100 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8 grid md:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
        <div className="md:sticky md:top-24">
          <div className={eyebrow + " mb-3"}>Real Stories</div>
          <h2 className="font-serif text-[clamp(2rem,3.6vw,3rem)] leading-[1.1] tracking-tight mb-5">
            Lives that have been <span className="italic font-light">forever changed.</span>
          </h2>
          <p className="text-tf-ink-soft leading-[1.6] mb-9">
            From children no longer anaphylactic to eggs and dairy, to adults sleeping through the night for the first time in years — these are the stories that keep us doing this work.
          </p>
          <div className="flex items-center gap-5 pt-5 border-t border-tf-ink/10">
            <div className="font-serif text-[2.6rem] text-tf-green-800 leading-none">4.9</div>
            <div>
              <div className="text-tf-wood text-base tracking-wider">★ ★ ★ ★ ★</div>
              <div className="text-xs text-tf-ink-soft mt-1">Based on family testimonials over 60 years of practice</div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className={`rounded-xl p-7 ${t.feature ? "bg-tf-green-800 text-tf-cream-50 sm:row-span-2 sm:flex sm:flex-col sm:justify-between" : "bg-tf-cream-50"}`}
            >
              <div className={`text-base tracking-wider mb-4 ${t.feature ? "text-tf-green-300" : "text-tf-wood"}`}>★ ★ ★ ★ ★</div>
              <p className={`font-serif text-[1.15rem] leading-[1.45] mb-6 ${t.feature ? "text-tf-cream-50" : "text-tf-ink"}`}>
                "{t.quote}"
              </p>
              <div className={`font-mono text-[0.7rem] tracking-[0.18em] uppercase ${t.feature ? "text-tf-green-300" : "text-tf-ink-soft"}`}>{t.author}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Letter ────────────────────────────────────────────────
function Letter() {
  return (
    <section className="bg-tf-cream-50 py-24 md:py-28">
      <div className="max-w-[760px] mx-auto px-8">
        <div className={eyebrow + " mb-4"}>A Letter From Jennie</div>
        <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight mb-8">
          To the parent <span className="italic font-light">who is tired.</span>
        </h2>
        {[
          "I know you're tired. You're scared. You're frustrated. Your heart hurts watching your child struggle with something they did not ask for.",
          "You've exhausted every option, every doctor, every elimination diet. You feel the guilt of watching them feel left out at birthday parties, festivals, hayrides.",
          "I see you. I've lived this journey with my own daughter — and I'm here to tell you it is not your fault, and there are answers that do not involve skin pricks, blood draws, or pharmaceuticals.",
          "You and your child deserve to live without allergies. We're here to make that possible.",
        ].map((p, i) => (
          <p key={i} className="text-tf-ink-soft leading-[1.75] text-[1.05rem] mb-5">{p}</p>
        ))}
        <div className="pt-8 mt-4 border-t border-tf-ink/10 font-serif text-[1.2rem] italic text-tf-green-800">
          Welcome to The Root Forum.<br />
          <strong className="not-italic text-tf-ink text-sm font-mono tracking-[0.18em] uppercase font-medium mt-3 inline-block">— Jennie Burks, Founder</strong>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ───────────────────────────────────────────────
function Pricing() {
  return (
    <section id="pricing" className="bg-tf-cream-100 py-24 md:py-28">
      <div className="max-w-[1240px] mx-auto px-8">
        <div className="text-center max-w-[640px] mx-auto mb-14">
          <div className={eyebrow + " mb-3"}>Investment</div>
          <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight mb-5">
            Care that pays off <span className="italic font-light">for a lifetime.</span>
          </h2>
          <p className="text-tf-ink-soft leading-[1.6]">
            We do not accept health insurance, but we do offer affordable payment plans for allergy patients.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5" data-testid="pricing-grid">
          {PRICING_GROUPS.map((g) => (
            <div key={g.label} className="bg-tf-cream-50 rounded-xl p-8 border border-tf-ink/8" data-testid={`pricing-group-${g.num}`}>
              <div className="flex items-baseline gap-4 mb-1">
                <span className="font-serif text-tf-green-700 text-[1.3rem] leading-none">{g.num}</span>
                <h3 className="font-serif text-[1.6rem] leading-none">{g.label}</h3>
              </div>
              <p className="text-tf-ink-soft text-sm italic mb-7 pl-10">{g.note}</p>
              <ul className="space-y-4">
                {g.items.map((item) => (
                  <li
                    key={item.name}
                    className={`flex items-start justify-between gap-6 pb-4 last:pb-0 last:border-0 border-b border-dashed border-tf-ink/12 ${item.featured ? "bg-tf-cream-100 -mx-4 px-4 py-3 rounded-md" : ""}`}
                  >
                    <div className="min-w-0">
                      <div className="font-serif text-[1.05rem] text-tf-ink leading-[1.3] mb-0.5 flex items-center gap-2">
                        {item.name}
                        {item.featured && <span className="font-mono text-[0.6rem] tracking-[0.18em] uppercase text-tf-green-800 bg-tf-green-300/50 px-2 py-0.5 rounded-full">Popular</span>}
                      </div>
                      <div className="font-mono text-[0.7rem] tracking-[0.1em] uppercase text-tf-ink-soft leading-[1.5]">{item.meta}</div>
                    </div>
                    <div className={`font-serif text-[1.6rem] leading-none whitespace-nowrap ${item.price === "Free" ? "text-tf-green-700 italic font-light" : "text-tf-ink"}`}>
                      {item.price}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href="#contact" className={btnPrimary} data-testid="pricing-contact">Ask a Question →</a>
          <p className="text-xs text-tf-ink-soft mt-5 italic">Follow us on Instagram or Facebook to keep up with monthly specials.</p>
        </div>
      </div>
    </section>
  );
}

// ─── Contact / Inquiry ────────────────────────────────────
function Contact() {
  return (
    <section id="contact" className="bg-tf-cream-100 py-24 md:py-28 border-t border-tf-ink/5">
      <div className="max-w-[980px] mx-auto px-8 grid md:grid-cols-2 gap-12 md:gap-16 items-start">
        <div>
          <div className={eyebrow + " mb-3"}>Have Questions?</div>
          <h2 className="font-serif text-[clamp(2rem,3.5vw,2.8rem)] leading-[1.1] tracking-tight mb-5">
            Send us a quick <span className="italic font-light">note</span>.
          </h2>
          <p className="text-tf-ink-soft leading-[1.65] text-base mb-8 max-w-md">
            Not ready to book yet? Tell us what's on your mind and we'll get back to you within one business day — by email or phone, whichever you prefer.
          </p>
          <ul className="space-y-3 text-sm text-tf-ink-soft">
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-tf-cream-50 flex items-center justify-center text-tf-green-800">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              <a href={`tel:${PHONE_TEL}`} className="hover:text-tf-green-800 transition-colors">{PHONE}</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-tf-cream-50 flex items-center justify-center text-tf-green-800">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </span>
              <a href="mailto:Jennie@therootforum.com" className="hover:text-tf-green-800 transition-colors">Jennie@therootforum.com</a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-tf-cream-50 flex items-center justify-center text-tf-green-800">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </span>
              <span>15322 Galaxie Ave, Suite 210 · Apple Valley, MN</span>
            </li>
          </ul>
        </div>
        <div className="bg-tf-cream-50 p-8 md:p-10 rounded-2xl border border-tf-ink/5">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "", topic: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/leads", {
        first: form.first.trim(),
        last: form.last.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.topic,
        notes: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError("We couldn't send your message just now. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-7 rounded-xl bg-tf-green-800 text-tf-cream-50" data-testid="contact-form-success">
        <div className="font-serif text-[1.4rem] mb-2">Thank you, {form.first || "friend"}.</div>
        <p className="text-tf-cream-200/85 text-sm leading-[1.6] mb-5">
          Your message landed in our inbox. Jennie will reach out within one business day.
        </p>
        <p className="text-tf-green-300 text-sm">
          Need to talk now? Call <a href={`tel:${PHONE_TEL}`} className="underline">{PHONE}</a>
        </p>
      </div>
    );
  }

  return (
    <form className="space-y-3.5" onSubmit={onSubmit} data-testid="contact-form">
      <div className="grid grid-cols-2 gap-3.5">
        <input className={input} placeholder="First Name" value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} required data-testid="contact-first" />
        <input className={input} placeholder="Last Name" value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} data-testid="contact-last" />
      </div>
      <input type="email" className={input} placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
      <input type="tel" className={input} placeholder="Phone Number (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
      <select className={input} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="contact-topic">
        <option value="">What's this about?</option>
        <option>Allergy Panel — questions</option>
        <option>Emotion / Body Code — questions</option>
        <option>Bio-Frequency Scan — questions</option>
        <option>Functional Bloodwork — questions</option>
        <option>Infrared Sauna — questions</option>
        <option>Packages &amp; pricing</option>
        <option>General inquiry</option>
      </select>
      <textarea
        className={input}
        placeholder="Tell us a bit about what you're hoping to address — no pressure to book."
        rows={5}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        required
        data-testid="contact-message"
      />
      {error && <p className="text-sm text-status-danger" data-testid="contact-error">{error}</p>}
      <button type="submit" disabled={submitting} className={`${btnPrimary} w-full justify-center mt-2 disabled:opacity-50`} data-testid="contact-submit">
        {submitting ? "Sending…" : "Send Inquiry →"}
      </button>
      <p className="text-xs text-tf-ink-soft text-center pt-2">
        We respond within one business day. Prefer to talk now? Call{" "}
        <a href={`tel:${PHONE_TEL}`} className="underline hover:text-tf-green-800">{PHONE}</a>
      </p>
    </form>
  );
}

// ─── FAQ ───────────────────────────────────────────────────
function Faq() {
  return (
    <section className="bg-tf-cream-50 py-24 md:py-28">
      <div className="max-w-[820px] mx-auto px-8">
        <div className="text-center mb-12">
          <div className={eyebrow + " mb-3"}>Got Questions?</div>
          <h2 className="font-serif text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.1] tracking-tight">
            Frequently asked <span className="italic font-light">questions.</span>
          </h2>
        </div>
        <div className="space-y-3" data-testid="faq-list">
          {FAQ.map((f, i) => (
            <details key={i} className="group bg-tf-cream-100 rounded-lg px-6 py-5 [&_summary]:cursor-pointer" data-testid={`faq-${i}`}>
              <summary className="flex items-center justify-between font-serif text-[1.1rem] list-none">
                <span>{f.q}</span>
                <span className="ml-4 text-tf-green-700 text-xl transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="text-tf-ink-soft text-[0.95rem] leading-[1.65] mt-4 pt-4 border-t border-tf-ink/8">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ─────────────────────────────────────────────
function FinalCta() {
  return (
    <section className="bg-tf-green-900 text-tf-cream-50 py-24 md:py-28 text-center relative overflow-hidden">
      <div className="max-w-[760px] mx-auto px-8 relative z-10">
        <div className="font-mono text-[0.7rem] tracking-[0.22em] uppercase text-tf-green-300 mb-4 font-medium">★ Ready When You Are</div>
        <h2 className="font-serif text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[1.08] tracking-tight mb-6">
          A healthier, <span className="italic font-light text-tf-green-300">more harmonious life</span> starts here.
        </h2>
        <p className="text-tf-cream-200/80 leading-[1.65] mb-10 text-[1.05rem]">Call or stop by — we'd love to meet you and your family.</p>
        <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-3 font-serif text-[clamp(1.8rem,3.5vw,2.6rem)] text-tf-cream-50 hover:text-tf-green-300 transition-colors" data-testid="cta-phone">
          <PhoneSvg className="w-7 h-7" /> {PHONE}
        </a>
        <p className="mt-6 text-tf-cream-200/80 text-[1.02rem]">
          or email at{" "}
          <a href="mailto:Jennie@therootforum.com" className="text-tf-cream-50 underline decoration-tf-green-300/60 underline-offset-[6px] hover:text-tf-green-300 transition-colors" data-testid="cta-email">
            Jennie@therootforum.com
          </a>
        </p>
      </div>
    </section>
  );
}

// ─── Footer ────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="bg-tf-ink text-tf-cream-200/70 py-14">
      <div className="max-w-[1240px] mx-auto px-8 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="font-serif text-[1.4rem] text-tf-cream-50 mb-3 flex items-center gap-2.5">
            <span className="relative w-[28px] h-[28px] rounded-full bg-tf-green-700 inline-block">
              <span className="absolute inset-[5px] rounded-full border border-tf-cream-50 [border-left-color:transparent] [border-right-color:transparent] rotate-[35deg]" />
            </span>
            The Root Forum
          </div>
          <p className="text-sm leading-[1.7] max-w-[380px]">{ADDRESS}</p>
          <p className="text-sm mt-3"><a href={`tel:${PHONE_TEL}`} className="hover:text-tf-cream-50">{PHONE}</a></p>
        </div>
        <div>
          <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-tf-cream-50 mb-4 font-medium">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#about" className="hover:text-tf-cream-50">About</a></li>
            <li><a href="#services" className="hover:text-tf-cream-50">Services</a></li>
            <li><a href="#team" className="hover:text-tf-cream-50">Meet the team</a></li>
            <li><a href="#pricing" className="hover:text-tf-cream-50">Pricing</a></li>
          </ul>
        </div>
        <div>
          <div className="font-mono text-[0.7rem] tracking-[0.18em] uppercase text-tf-cream-50 mb-4 font-medium">Connect</div>
          <ul className="space-y-2 text-sm">
            <li><a href="https://www.instagram.com/therootforum" target="_blank" rel="noreferrer" className="hover:text-tf-cream-50">Instagram</a></li>
            <li><a href="https://www.facebook.com/profile.php?id=61550486024278" target="_blank" rel="noreferrer" className="hover:text-tf-cream-50">Facebook</a></li>
            <li><a href="/admin/login" className="hover:text-tf-cream-50">Staff Login</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1240px] mx-auto px-8 mt-12 pt-6 border-t border-tf-cream-200/10 text-xs flex flex-wrap items-center justify-between gap-4">
        <span>© {new Date().getFullYear()} The Root Forum · We do not diagnose or treat disease.</span>
        <span className="font-mono tracking-wider">Made with care in Apple Valley, MN</span>
      </div>
    </footer>
  );
}

// ─── Shared ────────────────────────────────────────────────
const btnPrimary = "inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-tf-green-800 text-tf-cream-50 text-[0.88rem] font-medium tracking-wide hover:bg-tf-green-900 transition-all hover:-translate-y-px";
const btnGhost = "inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full border border-tf-ink/80 text-tf-ink text-[0.88rem] font-medium tracking-wide hover:bg-tf-ink hover:text-tf-cream-50 transition-all";
const btnCream = "inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-tf-cream-50 text-tf-green-800 text-[0.88rem] font-medium tracking-wide hover:bg-tf-cream-200 transition-all";
const eyebrow = "inline-block font-mono text-[0.72rem] tracking-[0.22em] uppercase text-tf-green-700 font-medium";
const input = "w-full px-4 py-3.5 bg-tf-cream-100 border border-tf-ink/8 rounded-lg text-[0.95rem] text-tf-ink placeholder:text-tf-ink-soft/60 focus:outline-none focus:border-tf-green-700 focus:ring-2 focus:ring-tf-green-700/20 transition-all font-sans";

function PhoneSvg({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function CircleDot({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
  );
}
